import { Ref } from 'vue'

import { useEventId } from '@desktop/views/Relation/composables/useEventId'
import type { IncidentUpdate } from '@desktop/views/Relation/utils'
import {
  queryOptions,
  useQuery,
  type DefinedInitialQueryOptionsWithDataTag,
  type UseQueryDefinedReturnType,
} from '@tanstack/vue-query'

type EventIncidentsUpdateQueryKey = readonly [
  'event',
  'incidentsUpdate',
  Ref<number>,
]

type EventIncidentsUpdateQueryOptions = DefinedInitialQueryOptionsWithDataTag<
  IncidentUpdate[],
  Error,
  IncidentUpdate[],
  EventIncidentsUpdateQueryKey
>

/**
 * Cache-only channel for the latest WebSocket incident differential batch.
 * Written by `writeIncidentBatch` via `setQueryData`; never fetched from Sports API.
 */
export function eventIncidentsUpdateQueryOptions(
  eventId: Ref<number>,
): EventIncidentsUpdateQueryOptions {
  return queryOptions({
    queryKey: ['event', 'incidentsUpdate', eventId] as const,
    queryFn: (): IncidentUpdate[] => [],
    initialData: [] as IncidentUpdate[],
  })
}

export const useEventIncidentsUpdateQuery = (
  options?: Omit<EventIncidentsUpdateQueryOptions, 'queryKey' | 'queryFn'>,
): UseQueryDefinedReturnType<IncidentUpdate[], Error> => {
  const eventId = useEventId()
  return useQuery<
    IncidentUpdate[],
    Error,
    IncidentUpdate[],
    EventIncidentsUpdateQueryKey
  >({
    ...eventIncidentsUpdateQueryOptions(eventId),
    ...options,
  })
}
